import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, 
  BookOpen, 
  Calendar, 
  CheckCircle2, 
  ChevronDown, 
  Compass, 
  Flag, 
  GraduationCap,
  Heart, 
  Info, 
  Map, 
  Medal, 
  Star, 
  Target, 
  Users,
  Shield,
  Globe2 as Globe,
  AlertTriangle,
  Monitor,
  Mountain,
  Sparkles,
  TreePine,
  Tent,
  Footprints
} from 'lucide-react';
import { useTranslation } from '../Context/TranslationContext';

const RoverSection = () => {
  const { isTamil } = useTranslation();
  const [expandedSection, setExpandedSection] = useState(null);

  const overviewContent = {
    intro: {
      en: "The Rover section is the senior most wing for boys aged 15 to 25 years, emphasizing adventure, leadership, and community service. Rovers develop advanced scouting skills and take on significant service projects through the Rover Crew.",
      ta: "ரோவர் பிரிவு 15 முதல் 25 வயது வரையிலான ஆண்களுக்கான மூத்த பிரிவாகும், சாகசம், தலைமைத்துவம் மற்றும் சமூக சேவையை வலியுறுத்துகிறது. ரோவர்கள் மேம்பட்ட சாரணர் திறன்களை வளர்த்து, ரோவர் குழு மூலம் முக்கியமான சேவை திட்டங்களை மேற்கொள்கின்றனர்."
    },
    keyFeatures: [
      {
        title: { en: "Age Group", ta: "வயது பிரிவு" },
        content: { en: "15 to 25 years", ta: "15 முதல் 25 வயது வரை" },
        icon: Users
      },
      {
        title: { en: "Unit Name", ta: "அலகு பெயர்" },
        content: { en: "Rover Crew", ta: "ரோவர் குழு" },
        icon: Flag
      },
      {
        title: { en: "Motto", ta: "குறிக்கோள்" },
        content: { en: "Service", ta: "சேவை" },
        icon: Heart
      }
    ],
    objectives: [
      {
        title: { en: "Advanced Scouting", ta: "மேம்பட்ட சாரணர் பயிற்சி" },
        content: { 
          en: "Mastering advanced outdoor skills, wilderness survival, and expedition leadership",
          ta: "மேம்பட்ட வெளிப்புற திறன்கள், வனவாழ்க்கை உயிர்வாழ்தல், மற்றும் பயண தலைமைத்துவத்தில் தேர்ச்சி"
        }
      },
      {
        title: { en: "Community Leadership", ta: "சமூக தலைமைத்துவம்" },
        content: {
          en: "Leading significant community service projects and mentoring younger sections",
          ta: "முக்கியமான சமூக சேவை திட்டங்களை வழிநடத்துதல் மற்றும் இளைய பிரிவுகளுக்கு வழிகாட்டுதல்"
        }
      },
      {
        title: { en: "Personal Development", ta: "தனிப்பட்ட வளர்ச்சி" },
        content: {
          en: "Building professional skills, character, and leadership qualities",
          ta: "தொழில்முறை திறன்கள், குணாதிசயம் மற்றும் தலைமைப் பண்புகளை வளர்த்தல்"
        }
      }
    ]
  };

  const sectionTitles = {
    overview: {
      title: { en: "Overview", ta: "கண்ணோட்டம்" },
    },
    advancement: {
      title: { en: "Advancement Scheme", ta: "முன்னேற்றத் திட்டம்" },
    },
    proficiencyBadges: {
      title: { en: "Proficiency Badges", ta: "திறன் பதக்கங்கள்" },
    }
  };

  const advancementScheme = [
    {
      title: { en: "Pravesh", ta: "பிரவேஷ்" },
      duration: { en: "Initial Stage", ta: "ஆரம்ப நிலை" },
      requirements: [
        { en: "Understanding Rover principles", ta: "ரோவர் கொள்கைகளை புரிந்துகொள்ளுதல்" },
        { en: "Basic service project participation", ta: "அடிப்படை சேவை திட்டப் பங்கேற்பு" },
        { en: "Physical fitness assessment", ta: "உடல் தகுதி மதிப்பீடு" },
        { en: "Crew integration activities", ta: "குழு ஒருங்கிணைப்பு செயல்பாடுகள்" }
      ],
      icon: Flag
    },
    {
      title: { en: "Nipun", ta: "நிபுண்" },
      duration: { en: "6 months", ta: "6 மாதங்கள்" },
      requirements: [
        { en: "Advanced wilderness skills", ta: "மேம்பட்ட வனவாழ்க்கை திறன்கள்" },
        { en: "Leadership in service projects", ta: "சேவை திட்டங்களில் தலைமைத்துவம்" },
        { en: "Specialized training completion", ta: "சிறப்பு பயிற்சி நிறைவு" },
        { en: "Mentoring younger sections", ta: "இளைய பிரிவுகளுக்கு வழிகாட்டுதல்" }
      ],
      icon: Mountain
    },
    {
      title: { en: "Rajya Puraskar", ta: "ராஜ்ய புரஸ்கார்" },
      duration: { en: "12 months", ta: "12 மாதங்கள்" },
      requirements: [
        { en: "Major community projects", ta: "பெரிய சமூக திட்டங்கள்" },
        { en: "Advanced emergency response", ta: "மேம்பட்ட அவசர மீட்பு" },
        { en: "Leadership development", ta: "தலைமைத்துவ மேம்பாடு" },
        { en: "Skill specialization", ta: "திறன் சிறப்பியல்பு" }
      ],
      icon: Award
    },
    {
      title: { en: "President's Rover", ta: "குடியரசுத் தலைவர் ரோவர்" },
      special: true,
      duration: { en: "Final Achievement", ta: "இறுதி சாதனை" },
      requirements: [
        { en: "Outstanding leadership demonstration", ta: "சிறந்த தலைமைத்துவ செயல்திறன்" },
        { en: "Significant community impact", ta: "குறிப்பிடத்தக்க சமூக தாக்கம்" },
        { en: "Advanced skill mastery", ta: "மேம்பட்ட திறன் தேர்ச்சி" },
        { en: "Exemplary service record", ta: "முன்மாதிரி சேவை பதிவு" }
      ],
      icon: Sparkles
    }
  ];

  const proficiencyBadges = [
    {
      category: { en: "Adventure Skills", ta: "சாகச திறன்கள்" },
      badges: [
        { name: { en: "Mountain Leader", ta: "மலை வழிகாட்டி" }, icon: Mountain },
        { name: { en: "Wilderness Expert", ta: "வனவாழ்க்கை நிபுணர்" }, icon: TreePine },
        { name: { en: "Adventure Guide", ta: "சாகச வழிகாட்டி" }, icon: Compass }
      ]
    },
    {
      category: { en: "Service Skills", ta: "சேவை திறன்கள்" },
      badges: [
        { name: { en: "Emergency Response", ta: "அவசர மீட்பு" }, icon: AlertTriangle },
        { name: { en: "Community Leader", ta: "சமூக தலைவர்" }, icon: Users },
        { name: { en: "First Aid Expert", ta: "முதலுதவி நிபுணர்" }, icon: Heart }
      ]
    },
    {
      category: { en: "Special Skills", ta: "சிறப்பு திறன்கள்" },
      badges: [
        { name: { en: "Project Manager", ta: "திட்ட மேலாளர்" }, icon: Target },
        { name: { en: "Expedition Leader", ta: "பயண தலைவர்" }, icon: Footprints },
        { name: { en: "Environmental Guide", ta: "சுற்றுச்சூழல் வழிகாட்டி" }, icon: Globe }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 mt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#004225] to-[#1B4D3E] py-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl font-bold mb-6">
              {isTamil ? "ரோவர் பிரிவு" : "Rover Section"}
            </h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              {overviewContent.intro[isTamil ? 'ta' : 'en']}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4">
        {/* Overview Section */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.overview.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#004225] mx-auto"></div>
          </div>

          {/* Key Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {overviewContent.keyFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-[#004225] bg-opacity-10 rounded-lg text-[#004225]">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold">
                    {feature.title[isTamil ? 'ta' : 'en']}
                  </h3>
                </div>
                <p className="text-gray-600">
                  {feature.content[isTamil ? 'ta' : 'en']}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Objectives */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {overviewContent.objectives.map((objective, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <h3 className="text-xl font-semibold mb-4">
                  {objective.title[isTamil ? 'ta' : 'en']}
                </h3>
                <p className="text-gray-600">
                  {objective.content[isTamil ? 'ta' : 'en']}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Advancement Section */}
        <section className="py-16 bg-white">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.advancement.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#004225] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advancementScheme.map((level, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-xl shadow-lg p-6 ${level.special ? 'border-2 border-yellow-500' : ''}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`p-3 rounded-lg ${level.special ? 'bg-yellow-50 text-yellow-600' : 'bg-[#004225] bg-opacity-10 text-[#004225]'}`}>
                    <level.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {level.title[isTamil ? 'ta' : 'en']}
                    </h3>
                    {level.duration && (
                      <p className="text-sm text-gray-500">
                        {level.duration[isTamil ? 'ta' : 'en']}
                      </p>
                    )}
                  </div>
                </div>
                <ul className="space-y-2">
                  {level.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        {req[isTamil ? 'ta' : 'en']}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Proficiency Badges Section */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.proficiencyBadges.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#004225] mx-auto"></div>
          </div>

          <div className="space-y-8">
            {proficiencyBadges.map((category, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-2xl font-semibold mb-6">
                  {category.category[isTamil ? 'ta' : 'en']}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {category.badges.map((badge, bIndex) => (
                    <div key={bIndex} className="flex items-center gap-4 bg-gray-50 rounded-lg p-4">
                      <div className="p-2 bg-[#004225] bg-opacity-10 rounded-lg text-[#004225]">
                        <badge.icon className="w-5 h-5" />
                      </div>
                      <span className="text-gray-700">
                        {badge.name[isTamil ? 'ta' : 'en']}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default RoverSection;